import { axiosClient, axiosStreamClient } from '@App/api/api-client'
import Pathes from '@App/dicts/pathes'
import { CatalogTransfer } from '@Entities/rightsholder/catalog-transfer'
import { Maybe } from '@Types'
import { ResultPage } from '@Entities/result-page'
import { CatalogTransferReviewDecisionStatus, CatalogTransferView } from '@Entities/rightsholder/catalog-transfer-view'
import { PaginationBody } from '@Entities/pagination'
import { WorksReportRequestDTO } from '@Entities/report/works-report-request'
import { WorksReportResponseDTO } from '@Entities/report/works-report-response'
import { CatalogTransferImportResult } from '@Entities/rightsholder/catalog-transfer-import-result'
import { CatalogTransferSearchQuery } from '@App/stores/search/models/catalog-transfer-search'

export const createCatalogTransfer = (rightsholderId: number, catalogTransferDTO: Partial<CatalogTransfer>) =>
    axiosClient.post<CatalogTransferView>(Pathes.RightsHolder.createCatalogTransfer(rightsholderId), catalogTransferDTO)

export const updateCatalogTransfer = (
    rightsholderId: number,
    catalogTransferId: number,
    catalogTransferDTO: Partial<CatalogTransfer>
) =>
    axiosClient.put<CatalogTransferView>(
        Pathes.RightsHolder.updateCatalogTransfer(rightsholderId, catalogTransferId),
        catalogTransferDTO
    )

export const submitCatalogTransfer = (rightsholderId: number, catalogTransferId: number) =>
    axiosClient.patch<CatalogTransferView>(Pathes.RightsHolder.submitCatalogTransfer(rightsholderId, catalogTransferId))

export const decideCatalogTransfer = (
    rightsholderId: number,
    catalogTransferId: number,
    decision: CatalogTransferReviewDecisionStatus,
    notes?: string
) =>
    axiosClient.post<CatalogTransferView>(
        Pathes.RightsHolder.catalogTransferDecision(rightsholderId, catalogTransferId),
        { decision, notes }
    )

export const linkCatalogTransferPublisher = (
    rightsholderId: number,
    catalogTransferId: number,
    publisherIpIds: number[]
) =>
    axiosClient.post(
        Pathes.RightsHolder.linkCatalogTransferPublisher(rightsholderId, catalogTransferId),
        publisherIpIds
    )

export const generateCatalogTransferDocumentUploadUrl = (
    rightsholderId: number,
    catalogTransferId: number,
    uploadType: 'CATALOG_IMPORT' | 'DOCUMENTATION',
    fileName: string,
    size: number,
    description: Maybe<string>
) =>
    axiosClient.post<{ presignedUrl: string; memberUploadId: number; catalogTransferMemberUploadId: number }>(
        Pathes.RightsHolder.uploadCatalogTransferDocument(rightsholderId, catalogTransferId),
        { name: fileName, size: size, description: description },
        { params: { uploadType: uploadType } }
    )

export const downloadCatalogTransferDocument = (
    rightsholderId: number,
    catalogTransferId: number,
    catalogTransferMemberUploadId: number
) =>
    axiosClient.get<{ downloadLink: string }>(
        Pathes.RightsHolder.downloadCatalogTransferDocument(
            rightsholderId,
            catalogTransferId,
            catalogTransferMemberUploadId
        )
    )

export const getCatalogTransfer = (rightsholderId: number, catalogTransferId: number) =>
    axiosClient.get<CatalogTransferView>(Pathes.RightsHolder.getCatalogTransfer(rightsholderId, catalogTransferId))

export const softDeleteCatalogTransfer = (rightsholderId: number, catalogTransferId: number) =>
    axiosClient.delete<void>(Pathes.RightsHolder.getCatalogTransfer(rightsholderId, catalogTransferId))

export const getCatalogTransferList = (
    rightsholderId: number,
    query: CatalogTransferSearchQuery,
    pagination: PaginationBody
) =>
    axiosClient.get<ResultPage<CatalogTransferView>>(
        Pathes.RightsHolder.getCatalogTransferList(rightsholderId, query, pagination)
    )

export const getCatalogTransferReviewList = (
    rightsholderId: number,
    query: CatalogTransferSearchQuery,
    pagination: PaginationBody
) =>
    axiosClient.get<ResultPage<CatalogTransferView>>(
        Pathes.RightsHolder.getCatalogTransferReviewList(rightsholderId, query, pagination)
    )

export const exportCatalogTransfer = (
    rightsholderId: number,
    catalogTransferId: number,
    reportRequest: WorksReportRequestDTO
) =>
    axiosClient.post<WorksReportResponseDTO>(
        Pathes.RightsHolder.exportCatalogTransferFile(rightsholderId, catalogTransferId),
        reportRequest
    )

export const importCatalogTransfer = (rightsholderId: number, catalogTransferId: number) =>
    axiosClient.post<CatalogTransferImportResult>(
        Pathes.RightsHolder.importCatalogTransferFile(rightsholderId, catalogTransferId)
    )

export const importCatalogTransferStream = (rightsholderId: number, catalogTransferId: number) =>
    axiosStreamClient.post<ReadableStream<BufferSource>>(
        Pathes.RightsHolder.importCatalogTransferFileStreaming(rightsholderId, catalogTransferId)
    )
